import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/Spinner";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { EmptyState } from "@/components/ui/EmptyState";
import type {
  FileResponse,
  InventionElementResponse,
  InventionElementsResponse,
} from "@patent-studio/api-types";
import { useNavigate } from "react-router";
import {
  useInventionElements,
  useInventionWrites,
  useSettleInvention,
  useStartExtraction,
} from "@/hooks/use-invention";
import { refusalDetail } from "@/services/api-client";
import { plural } from "@/lib/plural";
import { AddElementForm } from "./AddElementForm";
import { ElementCard } from "./ElementCard";
import { ElementKey } from "./ElementKey";
import { DifferenceCard } from "./DifferenceCard";
import { useQuestionGate } from "../questions/useQuestionGate";
import { useQuestions } from "@/hooks/use-questions";
import {
  CardAndBanner,
  StepBanner,
  SuggestedElements,
} from "../notifications/StepChanges";
import { CONTINUE, InventionFooter } from "./InventionFooter";
import { SubjectLine } from "./SubjectLine";
import { PageNames } from "./terms";
import type { LadderWrites } from "./ElementLadder";
import { useSourceDrawer } from "./source-drawer";
import { DocumentDrawer } from "../documents/DocumentDrawer";
import { arriveAt, useTermArrival } from "../terms/term-route";
import { elementCardId } from "./anchors";

/** "3 elements (1 requires your attention)", with the plural following the count. */
export function elementCount(total: number, attention: number): string {
  const elements = plural(total, "element");
  if (attention === 0) return elements;
  const verb = attention === 1 ? "requires" : "require";
  return `${elements} (${String(attention)} ${verb} your attention)`;
}

/**
 * The elements in the order the page draws them, which the server decides.
 *
 * The served order puts what needs attention first; the sequence order, which
 * the numbers follow, is the list itself. An id the order names that the list
 * does not carry is skipped rather than drawn empty.
 */
function displayed(
  data: InventionElementsResponse,
): InventionElementResponse[] {
  const byId = new Map(data.elements.map((e) => [e.element_id, e]));
  return data.display_order
    .map((id) => byId.get(id))
    .filter((e): e is InventionElementResponse => e !== undefined);
}

/**
 * What stands in for the list while there is none.
 *
 * The empty case is not a failure and must not read as one: until elements are
 * derived from the documents, every matter opens on it.
 */
function ListState({
  pending,
  failure,
  empty,
  extracting,
  onReread,
}: {
  pending: boolean;
  /** Why the read failed, or null while it has not. */
  failure: unknown;
  empty: boolean | undefined;
  extracting: boolean;
  onReread: () => void;
}) {
  if (pending) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        Reading the elements…
      </p>
    );
  }
  if (failure !== null) {
    return (
      <LoadFailure
        error={failure}
        fallback="The elements could not be read."
        className="mt-4"
        onRetry={onReread}
      />
    );
  }
  if (empty === true) {
    if (extracting) {
      // The wording is provisional: the design does not draw this state.
      return (
        <EmptyState data-testid="elements-extracting" className="mt-4">
          <Spinner />
          <span>
            Reading your intake documents for elements. Add your own below in
            the meantime.
          </span>
        </EmptyState>
      );
    }
    return (
      <EmptyState as="p" data-testid="elements-empty" className="mt-4">
        No elements yet. Add what the invention is made of, one element at a
        time.
      </EmptyState>
    );
  }
  return null;
}

const NOT_SAVED = "The server could not save it.";
const NOT_SETTLED = "The invention could not be confirmed.";

/** What the server said when it refused to settle, and the set it said it of. */
interface Refusal {
  version: number;
  text: string;
}

/**
 * Ask for the derivation once, for a matter that says it was never started.
 *
 * Intake fires the pass and navigates without waiting, so a request that never
 * landed leaves the attorney here with nothing to say why. The route is
 * idempotent, so asking again costs a matter already under way nothing and
 * rescues one that was not.
 */
function useAskedForExtraction(
  extraction: InventionElementsResponse["extraction"] | undefined,
  start: ReturnType<typeof useStartExtraction>,
) {
  const askedRef = useRef(false);
  useEffect(() => {
    if (extraction === "not_started" && !askedRef.current) {
      askedRef.current = true;
      start.mutate();
    }
  }, [extraction, start]);
}

/** A write the server would not take, said where the attorney made it. */
function WriteFailure({ failure }: { failure: unknown }) {
  return (
    <p role="alert" className="mt-3 text-xs text-destructive">
      That change could not be saved. {refusalDetail(failure, NOT_SAVED)}
    </p>
  );
}

/** The heading, the count once the set is read, and the key to the tags. */
function PanelHeading({
  data,
}: {
  data: InventionElementsResponse | undefined;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="text-base font-bold tracking-tight">
          Elements of the Invention
        </h2>
        {/* Absent until the set is read, rather than an empty space where a
            count is about to appear. */}
        {data !== undefined && (
          <span
            data-testid="element-count"
            className="text-[10.5px] font-semibold text-muted-foreground"
          >
            {elementCount(data.total, data.requires_attention)}
          </span>
        )}
        <ElementKey />
      </div>
      <p className="mt-1.5 max-w-[640px] text-xs leading-relaxed text-muted-foreground">
        Each element is tagged with its source, so everything is traceable to
        specific intake documentation. Anything not traceable to the disclosure
        documents requires your confirmation before proceeding.
      </p>
    </>
  );
}

/**
 * Start the read again: the route starts a matter that failed or never started,
 * so the words "start it again" have a control beside them.
 */
function TryAgain({ start }: { start: ReturnType<typeof useStartExtraction> }) {
  return (
    <Button
      variant="link"
      disabled={start.isPending}
      onClick={() => {
        start.mutate();
      }}
    >
      {start.isPending ? "Starting again…" : "Try again"}
    </Button>
  );
}

/** How the read of the documents is going, once there is already a set to say it beside. */
function ExtractionNotes({
  data,
  start,
}: {
  data: InventionElementsResponse | undefined;
  start: ReturnType<typeof useStartExtraction>;
}) {
  return (
    <>
      {data !== undefined &&
        data.elements.length > 0 &&
        data.extraction === "running" && (
          <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Spinner size="sm" />
            Still reading your intake documents. More elements may appear.
          </p>
        )}
      {data?.extraction === "failed" && !start.isError && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          The elements could not be read from your documents.{" "}
          {data.extraction_error ?? "Add what the invention is made of below."}{" "}
          <TryAgain start={start} />
        </p>
      )}
      {data !== undefined && data.dropped_fallbacks > 0 && (
        <p
          data-testid="dropped-fallbacks"
          className="mt-4 text-xs text-muted-foreground"
        >
          {plural(data.dropped_fallbacks, "fallback")} the read proposed could
          not be found in the documents and{" "}
          {data.dropped_fallbacks === 1 ? "was" : "were"} left out. Add any you
          know of under the element they narrow.
        </p>
      )}
      {start.isError && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          Reading your documents for elements could not be started. You can add
          what the invention is made of below. <TryAgain start={start} />
        </p>
      )}
    </>
  );
}

/** The writes open on one element's ladder, each naming the element. */
function ladderWrites(
  elementId: string,
  writes: ReturnType<typeof useInventionWrites>,
): LadderWrites {
  return {
    onAdd: (rung) =>
      writes.addFallback.mutateAsync({
        elementId,
        kind: rung.kind,
        text: rung.text,
        parentId: rung.parentId,
        declaredSource: rung.declaredSource,
      }),
    onAmend: (fallbackId, text) =>
      writes.amendFallback.mutateAsync({ elementId, fallbackId, text }),
    onName: (fallbackId, term) =>
      writes.nameFallback.mutateAsync({ elementId, fallbackId, term }),
    onRemove: (fallbackId) => {
      writes.removeFallback.mutate({ elementId, fallbackId });
    },
  };
}

/** The set as the page draws it: attention first, one editor open at a time. */
function ElementList({
  data,
  busy,
  amendingId,
  onAmendingId,
  writes,
  openable,
  onOpenSource,
}: {
  data: InventionElementsResponse;
  busy: boolean;
  amendingId: string | null;
  onAmendingId: (id: string | null) => void;
  writes: ReturnType<typeof useInventionWrites>;
  openable: ReadonlyMap<string, FileResponse>;
  onOpenSource: (element: InventionElementResponse, fileId: string) => void;
}) {
  return (
    <ul className="mt-4 space-y-1.5">
      {displayed(data).map((element) => (
        <ElementCard
          key={element.element_id}
          element={element}
          busy={busy}
          amending={amendingId === element.element_id}
          amendLocked={amendingId !== null && amendingId !== element.element_id}
          only={data.elements.length === 1}
          onAmendOpen={() => {
            onAmendingId(element.element_id);
          }}
          onAmendClose={() => {
            onAmendingId(null);
          }}
          onConfirm={() => {
            writes.confirm.mutate(element.element_id);
          }}
          onAmend={(text) =>
            writes.amend.mutateAsync({ elementId: element.element_id, text })
          }
          onName={(term) =>
            writes.nameElement.mutateAsync({
              elementId: element.element_id,
              term,
            })
          }
          onRelate={(relation) =>
            relation === null
              ? writes.unrelate.mutateAsync(element.element_id)
              : writes.relate.mutateAsync({
                  elementId: element.element_id,
                  ...relation,
                })
          }
          elements={data.elements}
          onRemove={() => {
            writes.remove.mutate(element.element_id);
          }}
          openable={openable}
          onOpenSource={(fileId) => {
            onOpenSource(element, fileId);
          }}
          ladder={ladderWrites(element.element_id, writes)}
        />
      ))}
    </ul>
  );
}

/**
 * The gate out of Invention, and the refusal that came back from asking for it.
 *
 * The refusal is held against the version of the set it was about: it re-reads
 * the set, so once any write has moved that version the words describe a page
 * that is gone.
 */
function ConnectedFooter({
  data,
  taskId,
}: {
  data: InventionElementsResponse;
  taskId: string | null;
}) {
  const navigate = useNavigate();
  const settle = useSettleInvention(taskId);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const gate = useQuestionGate({
    taskId: taskId ?? "",
    trigger: "invention_lock",
    topic: "invention",
    cta: CONTINUE,
    commit: () => {
      if (taskId) void navigate(`/drafts/${taskId}/thesis/novelty`);
    },
    // Continue asks once the elements are confirmed.
    ready: data.gate.settled,
    reading: String(data.version),
  });
  if (gate.card !== null) {
    return taskId === null ? (
      gate.card
    ) : (
      <CardAndBanner card={gate.card} taskId={taskId} step="invention" />
    );
  }
  return (
    <InventionFooter
      gate={data.gate}
      elements={data.elements}
      settling={settle.isPending}
      refusal={
        refused !== null && refused.version === data.version
          ? refused.text
          : null
      }
      onSettle={() => {
        const { version } = data;
        settle.mutate(undefined, {
          onError: (error) => {
            setRefused({ version, text: refusalDetail(error, NOT_SETTLED) });
          },
        });
      }}
      asking={gate.asking}
      onContinue={gate.press}
      above={taskId !== null && <StepBanner taskId={taskId} step="invention" />}
    />
  );
}

/**
 * Sent here by the Terminology panel: the element is shown, and opened when its
 * Edit sent it.
 *
 * The arrival lands with the commit that draws the card, rather than on the
 * next frame, which a reader of the opened editor can come before. It is held
 * until a commit draws the card, as the Define page holds its row.
 */
function useElementArrival(
  data: InventionElementsResponse | undefined,
  edit: (elementId: string) => void,
) {
  const arrivingRef = useRef<string | null>(null);
  useTermArrival(data !== undefined, ({ recordId, edit: editing }) => {
    if (!data?.elements.some((e) => e.element_id === recordId)) return;
    if (editing) edit(recordId);
    arrivingRef.current = recordId;
  });
  useEffect(() => {
    const recordId = arrivingRef.current;
    // Not drawn yet: this runs again after the commit that draws it.
    if (
      recordId === null ||
      document.getElementById(elementCardId(recordId)) === null
    ) {
      return;
    }
    arrivingRef.current = null;
    arriveAt(elementCardId(recordId));
  });
}

/**
 * The elements list, its heading and the control that adds to it.
 *
 * Split from the page because the page is the heading and the intro; this is the
 * whole of what the attorney works with.
 */
export function ElementsPanel({ taskId }: { taskId: string | null }) {
  const { data, isPending, error, refetch } = useInventionElements(taskId);
  const drawer = useSourceDrawer(taskId);
  const writes = useInventionWrites(taskId);
  const { add, busy, lastError: failure } = writes;
  // The gaps the derivation raised wait in the question store (lane L decision 39).
  const questions = useQuestions(taskId ?? "");
  const start = useStartExtraction(taskId);
  useAskedForExtraction(data?.extraction, start);
  // One editor at a time: the element whose wording is open for amendment.
  const [amendingId, setAmendingId] = useState<string | null>(null);
  useElementArrival(data, setAmendingId);

  return (
    <div className="mt-8">
      <PanelHeading data={data} />

      <ListState
        pending={isPending}
        failure={error}
        empty={data?.elements.length === 0}
        extracting={data?.extraction === "running"}
        onReread={() => void refetch()}
      />

      <ExtractionNotes data={data} start={start} />

      {data !== undefined && data.elements.length > 0 && (
        <>
          <SubjectLine
            subject={data.subject}
            busy={busy}
            onSave={(subject) => writes.subject.mutateAsync(subject)}
          />
          {taskId !== null && <SuggestedElements taskId={taskId} />}
          <PageNames taskId={taskId} elements={data.elements}>
            <ElementList
              data={data}
              busy={busy}
              amendingId={amendingId}
              onAmendingId={setAmendingId}
              writes={writes}
              openable={drawer.onTask}
              onOpenSource={drawer.open}
            />
          </PageNames>
        </>
      )}

      {taskId !== null && (
        <DocumentDrawer
          taskId={taskId}
          documents={drawer.viewing?.documents ?? null}
          openAt={drawer.viewing?.openAt}
          onClose={drawer.close}
        />
      )}

      <AddElementForm
        busy={busy}
        elements={data?.elements ?? []}
        onAdd={(added) => add.mutateAsync(added)}
      />

      {/* A failed write leaves the list as the server last served it, so what is
          on screen is still true. What it cannot do is stay silent. */}
      {failure != null && <WriteFailure failure={failure} />}

      {/* Drawn below the list it compares, so the attorney reads the elements first. */}
      {data !== undefined && data.elements.length > 0 && (
        <DifferenceCard
          agreementNote={data.agreement_note}
          elements={data.elements}
          questions={questions.data?.questions ?? []}
        />
      )}

      {/* Absent until the set is read: a gate drawn before the server has
          spoken would name conditions it does not know. */}
      {data !== undefined && <ConnectedFooter data={data} taskId={taskId} />}
    </div>
  );
}
